import { Fragment, useEffect, useMemo, useState } from "react";

import { useBelegZeilen } from "../../hooks/useBelegZeilen";
import { useBibliothekSchreiben } from "../../hooks/useBibliothekSchreiben";
import { euro, type WebInvoiceDraft } from "../../lib/invoices";
import { newInvoiceLine } from "../../lib/invoiceEditor";
import {
  BLOCK_KIND,
  dupliziereBlock,
  entferneBlock,
  fasseZuBlockZusammen,
  gruppeNetto,
  gruppeOptionalNetto,
  gruppiereZeilen,
  loeseBlockAuf,
  positionsNummer,
  setzeBausteinInBlock,
  zeileAusKatalog,
  zeilenAusKatalogBlock,
} from "../../lib/invoiceBlocks";
import { ablageErlaubt, verschiebeZu } from "../../lib/invoiceUmordnen";
import { useInvoiceClipboard } from "../../hooks/useInvoiceClipboard";
import { useLineSelection } from "../../hooks/useLineSelection";
import {
  quelleBeschriften,
  zeilenAusEintraegen,
  type AblageEintrag,
} from "../../lib/invoiceClipboard";
import type { PackzettelPosition } from "../../lib/packzettel";
import InvoiceBlockHead from "./InvoiceBlockHead";
import InvoiceEinfuegepunkt from "./InvoiceEinfuegepunkt";
import InvoiceGliederung from "./InvoiceGliederung";
import PackzettelDialog from "./PackzettelDialog";
import PackzettelListe from "./PackzettelListe";
import InvoiceClipboardPanel from "./InvoiceClipboardPanel";
import InvoiceLineRow from "./InvoiceLineRow";
import InvoiceLineRowFlat from "./InvoiceLineRowFlat";
import InvoiceSelectionBar from "./InvoiceSelectionBar";

type Props = {
  draft: WebInvoiceDraft;
  units: string[];
  /** Für den Packzettel: an welchen Entwurf bzw. Beleg er gehängt wird. */
  entwurfId?: string;
  rechnungId?: string;
  onChange: (draft: WebInvoiceDraft) => void;
  /**
   * Kopf-Aktionen der Seite — zusätzlich hier, dezent im Stil von Index und
   * Zwischenablage, weil diese Leiste beim Scrollen oben kleben bleibt und
   * der Seitenkopf nicht (Benjamin, 01.09.2026: „damit der Nutzer sie
   * ebenfalls immer erreicht"). Dieselben Handler wie oben — keine eigene
   * Logik hier.
   */
  onPreview?: () => void;
  previewBusy?: boolean;
  onFinalize?: () => void;
  finalizeLabel?: string;
  finalizeDisabled?: boolean;
  onDiscard?: () => void;
};

/**
 * Positionen und Blöcke eines Belegs.
 *
 * Die Zeilenliste bleibt flach — die sichtbare Gruppierung wird bei jedem
 * Rendern aus ihr abgeleitet (`gruppiereZeilen`), mit derselben Klammerregel,
 * die die PDF-Engine anwendet. So kann der Editor keine andere Zwischensumme
 * zeigen als das fertige Dokument.
 *
 * Jede Zeile hat zwei Gesichter: flach gelesen (`InvoiceLineRowFlat`,
 * `InvoiceBlockHead` im Ruhezustand) wie eine Tabelle, und auf Klick ein
 * Formular (`InvoiceLineRow`, derselbe Block-Kopf im Bearbeiten-Zustand) —
 * immer nur eine Zeile gleichzeitig (Claude Design, 01.09.2026: „flaches
 * Design + inline edit on select"). Verschoben wird per Ziehen direkt hier,
 * mit derselben Ablagelogik wie im Index (`invoiceUmordnen.ts`) — beide
 * Ansichten teilen sich dieselbe Bedienung.
 */
export default function InvoicePositionEditor({
  draft, units, entwurfId = "", rechnungId = "", onChange,
  onPreview, previewBusy, onFinalize, finalizeLabel, finalizeDisabled, onDiscard,
}: Props) {
  const [notiz, setNotiz] = useState("");
  const [indexOffen, setIndexOffen] = useState(false);
  const [ablageOffen, setAblageOffen] = useState(false);
  /**
   * Eingeklappte Blöcke — dieselbe Menge in der Gesamtansicht und im Index,
   * damit „Block auf/zu" überall dasselbe bedeutet (Benjamin, 01.09.2026:
   * „das steigert die Übersicht"). Rein flüchtiger UI-Zustand, keine
   * Belegdaten — daher lokal statt in `draft.line_rows`.
   */
  const [eingeklappt, setEingeklappt] = useState<Set<string>>(new Set());
  const umschaltenEingeklappt = (id: string) => setEingeklappt((vorher) => {
    const naechste = new Set(vorher);
    if (naechste.has(id)) naechste.delete(id); else naechste.add(id);
    return naechste;
  });
  /** Die eine Zeile (Position oder Block), die gerade als Formular offen ist. */
  const [bearbeiteteZeile, setBearbeiteteZeile] = useState<string | null>(null);
  /** Absoluter Index in `draft.line_rows`, der gerade gezogen wird; -1 = nichts. */
  const [zieht, setZieht] = useState(-1);
  const [packzettelVorlage, setPackzettelVorlage] = useState<PackzettelPosition[] | null>(null);
  const ablage = useInvoiceClipboard();
  const wahl = useLineSelection(draft.line_rows);

  const unitOptions = useMemo(
    () => [...new Set([...units, ...draft.line_rows.map((line) => line.unit_label)].filter(Boolean))],
    [units, draft.line_rows],
  );
  const gruppen = useMemo(() => gruppiereZeilen(draft.line_rows), [draft.line_rows]);
  const zeilen = useBelegZeilen(draft, onChange);
  const bibliothek = useBibliothekSchreiben(setNotiz);

  // Escape schließt das offene Formular — ohne eigenen Schließen-Knopf
  // bräuchte man sonst immer eine andere Zeile zum Anklicken.
  useEffect(() => {
    if (!bearbeiteteZeile) return;
    const aufEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setBearbeiteteZeile(null); };
    document.addEventListener("keydown", aufEscape);
    return () => document.removeEventListener("keydown", aufEscape);
  }, [bearbeiteteZeile]);

  const inAblage = () => {
    const eintraege = wahl.alsAblageEintraege(
      quelleBeschriften(draft.kind, draft.doc_number),
    );
    ablage.ergaenzen(eintraege);
    wahl.aufheben();
    setNotiz(`${eintraege.length} ${eintraege.length === 1 ? "Eintrag" : "Einträge"} in der Zwischenablage.`);
  };

  /** Aus der Ablage ans Ende anhängen — nie ersetzen, wie bei den Vorlagen. */
  const ausAblageEinfuegen = (eintraege: AblageEintrag[]) => {
    const neu = zeilenAusEintraegen(eintraege);
    zeilen.anhaengen(neu);
    setAblageOffen(false);
    setNotiz(`${neu.length} ${neu.length === 1 ? "Zeile" : "Zeilen"} eingefügt.`);
  };

  const berechnet = draft.line_rows.filter((line) => line.line_kind === "position").length;
  const optional = draft.line_rows.filter((line) => line.line_kind === "optional").length;

  /** Griff-Ereignisse für eine Zeile an ihrer absoluten Stelle in `line_rows`. */
  const ziehenFuer = (index: number) => ({
    onDragStart: (event: React.DragEvent) => {
      setZieht(index);
      event.dataTransfer.effectAllowed = "move";
      // Firefox startet ohne gesetzte Daten gar keinen Zug.
      event.dataTransfer.setData("text/plain", draft.line_rows[index]?.id || "");
    },
    onDragEnd: () => setZieht(-1),
    ziehtGerade: zieht === index,
  });

  const zeilenBlock = (gruppe: (typeof gruppen)[number]) => gruppe.zeilen.map(({ line, index }) => (
    <Fragment key={line.id}>
    {bearbeiteteZeile === line.id ? (
      <InvoiceLineRow
        line={line}
        nummer={positionsNummer(draft.line_rows, index)}
        unitOptions={unitOptions}
        katalogLaeuft={bibliothek.laeuft}
        gewaehlt={wahl.gewaehlt(line.id)}
        onToggleWahl={() => wahl.umschalten(line.id)}
        onChange={(patch) => zeilen.aendern(index, patch)}
        onToggleOptional={() => zeilen.aendern(index, {
          line_kind: line.line_kind === "optional" ? "position" : "optional",
        })}
        onDuplicate={() => zeilen.duplizieren(index)}
        onSaveToCatalog={() => bibliothek.positionSchreiben(line)}
        onRemove={() => zeilen.entfernen(index)}
        onKatalogPosition={(item) => zeilen.aendern(index, {
          ...zeileAusKatalog(item, line.line_kind), id: line.id,
        })}
        onKatalogBlock={(block) => {
          // Der Baustein ersetzt die angefangene Zeile an ihrer Stelle — sonst
          // stünde eine leere Position über dem eben eingefügten Block.
          zeilen.ersetzen(index, zeilenAusKatalogBlock(block));
          setNotiz(`Baustein „${block.title}" mit ${block.lines.length} Positionen übernommen.`);
        }}
      />
    ) : (
      <InvoiceLineRowFlat
        line={line}
        nummer={positionsNummer(draft.line_rows, index)}
        gewaehlt={wahl.gewaehlt(line.id)}
        onToggleWahl={() => wahl.umschalten(line.id)}
        onBearbeiten={() => setBearbeiteteZeile(line.id)}
        onToggleOptional={() => zeilen.aendern(index, {
          line_kind: line.line_kind === "optional" ? "position" : "optional",
        })}
        onDuplicate={() => zeilen.duplizieren(index)}
        onSaveToCatalog={() => bibliothek.positionSchreiben(line)}
        onRemove={() => zeilen.entfernen(index)}
        katalogLaeuft={bibliothek.laeuft}
        ziehen={ziehenFuer(index)}
      />
    )}
    {einfuegePunkt(index + 1)}
    </Fragment>
  ));

  const einfuegePunkt = (stelle: number) => (
    <InvoiceEinfuegepunkt
      onPosition={() => zeilen.einfuegenBei(stelle, [newInvoiceLine()])}
      onOptional={() => zeilen.einfuegenBei(stelle, [newInvoiceLine("optional")])}
      onBlock={() => zeilen.einfuegenBei(stelle, [newInvoiceLine(BLOCK_KIND)])}
      ziehtAktiv={zieht >= 0}
      ablageErlaubt={zieht >= 0 && ablageErlaubt(draft.line_rows, zieht, stelle)}
      onDropZiel={() => { zeilen.setzen(verschiebeZu(draft.line_rows, zieht, stelle)); setZieht(-1); }}
    />
  );

  return (
    <section className="invoice-editor-card invoice-position-card">
      <div className="invoice-editor-card-head">
        <div><p className="invoice-kicker">Leistungen</p><h2>Positionen</h2></div>
        <span className="invoice-position-head-right">
          {berechnet} berechnet{optional ? ` · ${optional} optional` : ""}
          {/* Dieselben drei Aktionen wie im Seitenkopf, hier nur dezent im
              schlichten Buttonstil — kein eigener Zustand, keine eigene
              Logik, damit sie beim Scrollen mitwandern und trotzdem nicht
              lauter wirken als Index/Zwischenablage. */}
          {onPreview && (
            <button type="button" className="invoice-button invoice-button-quiet" onClick={onPreview} disabled={previewBusy}>
              {previewBusy ? "Erzeugt PDF …" : "PDF-Vorschau"}
            </button>
          )}
          {onFinalize && (
            <button type="button" className="invoice-button invoice-button-quiet" onClick={onFinalize} disabled={finalizeDisabled}>
              {finalizeLabel || "Festschreiben"}
            </button>
          )}
          {onDiscard && (
            <button type="button" className="invoice-button invoice-button-quiet" onClick={onDiscard}>
              Entwurf verwerfen
            </button>
          )}
          <button
            type="button"
            className={"invoice-button" + (indexOffen ? " is-gefuellt" : "")}
            onClick={() => setIndexOffen((v) => !v)}
            aria-pressed={indexOffen}
          >
            Index
          </button>
          <button
            type="button"
            className={"invoice-button" + (ablage.eintraege.length ? " is-gefuellt" : "")}
            onClick={() => setAblageOffen(true)}
          >
            Zwischenablage
            {ablage.eintraege.length > 0 && <b>{ablage.eintraege.length}</b>}
          </button>
        </span>
      </div>

      {notiz && <p className="invoice-inline-note" role="status">{notiz}</p>}

      {/* Der Index steht VOR der Liste: hinter ihr lag er bei mehr als drei
          Positionen außerhalb des Bildschirms, und der Knopf sah wirkungslos
          aus (Benjamin, 01.09.2026). */}
      {indexOffen && (
        <InvoiceGliederung
          lines={draft.line_rows}
          onChange={zeilen.setzen}
          onSchliessen={() => setIndexOffen(false)}
          eingeklappt={eingeklappt}
          onToggleEingeklappt={umschaltenEingeklappt}
        />
      )}

      {/* Spaltenkopf wie im PDF — einmal für die ganze Liste, nicht je Block:
          eine Positionsliste soll sich wie eine Tabelle lesen. */}
      <div className="invoice-position-kopfzeile" aria-hidden="true">
        <span />
        <span />
        <span className="invoice-position-kopfzeile-inhalt">
          <span>#</span>
          <span>Leistung</span>
          <span>Menge</span>
          <span>Einheit</span>
          <span>Einzel netto</span>
          <span>USt.</span>
          <span>Netto</span>
        </span>
        <span />
      </div>

      <div
        className="invoice-position-list"
        onClick={(event) => { if (event.target === event.currentTarget) setBearbeiteteZeile(null); }}
        onKeyDown={(event) => {
          // Enter in einem Feld schließt die Zeile (Benjamin, 02.09.2026) —
          // außer im Mehrzeiler, wo Enter ein Absatz ist, und außer wenn die
          // Titelsuche die Taste schon für einen Vorschlag verbraucht hat.
          if (event.key !== "Enter" || event.defaultPrevented) return;
          if ((event.target as HTMLElement).tagName === "TEXTAREA") return;
          if (!bearbeiteteZeile) return;
          event.preventDefault();
          setBearbeiteteZeile(null);
        }}
      >
        {einfuegePunkt(0)}
        {gruppen.map((gruppe) => gruppe.block ? (
          <div className="invoice-block" key={gruppe.block.id}>
            <InvoiceBlockHead
              block={gruppe.block}
              anzahl={gruppe.zeilen.length}
              katalogLaeuft={bibliothek.laeuft}
              gewaehlt={wahl.gewaehlt(gruppe.block.id)}
              eingeklappt={eingeklappt.has(gruppe.block.id)}
              onToggleEingeklappt={() => umschaltenEingeklappt(gruppe.block!.id)}
              bearbeitet={bearbeiteteZeile === gruppe.block.id}
              onBearbeiten={() => setBearbeiteteZeile(gruppe.block!.id)}
              onToggleWahl={() => wahl.umschalten(gruppe.block!.id)}
              onChange={(patch) => zeilen.aendern(gruppe.blockIndex, patch)}
              onDuplicate={() => zeilen.setzen(dupliziereBlock(draft.line_rows, gruppe.blockIndex))}
              onSaveToCatalog={() => bibliothek.blockSchreiben(gruppe)}
              onKatalogBlock={(baustein) => {
                // Der Baustein füllt DIESEN Blockkopf und hängt seine
                // Positionen unten an — `zeilenAusKatalogBlock` brächte einen
                // zweiten Kopf mit und zerlegte den Block in zwei.
                zeilen.setzen(setzeBausteinInBlock(draft.line_rows, gruppe.blockIndex, baustein));
                setNotiz(`Baustein „${baustein.title}" übernommen: ${baustein.lines.length} Positionen.`);
              }}
              onDissolve={() => zeilen.setzen(loeseBlockAuf(draft.line_rows, gruppe.blockIndex))}
              onRemove={() => {
                const rest = entferneBlock(draft.line_rows, gruppe.blockIndex);
                zeilen.setzen(rest.length ? rest : [newInvoiceLine()]);
              }}
              ziehen={ziehenFuer(gruppe.blockIndex)}
            />
            {!eingeklappt.has(gruppe.block.id) && (
              <>
                <div className="invoice-block-lines">
                  {gruppe.zeilen.length === 0 && (
                    <>
                      <p className="invoice-block-leer">
                        Noch keine Position in diesem Block — die nächste hinzugefügte landet hier.
                      </p>
                      {einfuegePunkt(gruppe.blockIndex + 1)}
                    </>
                  )}
                  {zeilenBlock(gruppe)}
                </div>
                {gruppe.zeilen.length > 0 && (
                  <div className="invoice-block-zwischensumme">
                    <span>Zwischensumme {gruppe.block.title.trim() || "ohne Überschrift"}</span>
                    <strong>{euro(gruppeNetto(gruppe))}</strong>
                  </div>
                )}
                {/* Spiegelbild der PDF-Zeile „Summe Optionalpositionen"
                    (`render_pdf._flush_section_subtotal`) — der Editor darf
                    keine andere Zahl zeigen als das fertige Dokument. */}
                {gruppeOptionalNetto(gruppe) > 0 && (
                  <div className="invoice-block-zwischensumme ist-optional">
                    <span>Summe Optionalpositionen</span>
                    <strong>{euro(gruppeOptionalNetto(gruppe))}</strong>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="invoice-block is-frei" key="frei">{zeilenBlock(gruppe)}</div>
        ))}
      </div>

      {wahl.hatAuswahl && (
        <InvoiceSelectionBar
          anzahlPositionen={wahl.zusammenfassung.positionen}
          anzahlBloecke={wahl.zusammenfassung.bloecke}
          anzahlZeilen={wahl.zusammenfassung.zeilen}
          onKopieren={inAblage}
          onZuBlock={() => {
            const ergebnis = fasseZuBlockZusammen(draft.line_rows, wahl.ids);
            if (!ergebnis) return;
            zeilen.setzen(ergebnis.lines);
            wahl.aufheben();
            // Der neue Block ist noch namenlos — sein Formular geht direkt auf.
            setBearbeiteteZeile(ergebnis.block.id);
          }}
          onPackzettel={() => {
            const positionen = wahl.alsPackzettelPositionen();
            if (!positionen.length) {
              setNotiz("Für einen Packzettel braucht es mindestens eine berechnete Position.");
              return;
            }
            // Ein Packzettel hängt am Angebot. Bei einem noch nie gespeicherten
            // Entwurf gibt es nichts, woran er hängen könnte — das Speichern
            // läuft ohnehin 2,5 s nach der letzten Änderung von allein an.
            if (!entwurfId && !rechnungId) {
              setNotiz("Der Entwurf muss einmal gespeichert sein, bevor ein Packzettel daran hängen kann.");
              return;
            }
            setPackzettelVorlage(positionen);
          }}
          onAufheben={wahl.aufheben}
        />
      )}

      <PackzettelListe entwurfId={entwurfId} rechnungId={rechnungId} />

      <footer className="invoice-position-actions">
        <button className="invoice-button" type="button" onClick={() => zeilen.anhaengen([newInvoiceLine()])}>+ Position</button>
        <button className="invoice-button" type="button" onClick={() => zeilen.anhaengen([newInvoiceLine("optional")])}>+ Optionale Position</button>
        <button className="invoice-button" type="button" onClick={() => zeilen.anhaengen([newInvoiceLine(BLOCK_KIND)])}>+ Block</button>
      </footer>

      <InvoiceClipboardPanel
        eintraege={ablage.eintraege}
        offen={ablageOffen}
        onSchliessen={() => setAblageOffen(false)}
        onEinfuegen={ausAblageEinfuegen}
        onEntfernen={ablage.entfernen}
        onLeeren={ablage.leeren}
      />

      {/* Bedingt gemountet, damit jede Erstellung mit frischen Budgets
          beginnt statt mit denen der letzten, verworfenen Auswahl. */}
      {packzettelVorlage && (
        <PackzettelDialog
          vorlage={packzettelVorlage}
          entwurfId={entwurfId}
          rechnungId={rechnungId}
          betreffVorschlag={draft.doc_title || draft.subject || draft.doc_number}
          onSchliessen={() => setPackzettelVorlage(null)}
          onErstellt={() => { wahl.aufheben(); setNotiz("Packzettel erstellt."); }}
        />
      )}
    </section>
  );
}
